/**
 * Deliberate cleanup of mirrored work Todoist no longer has.
 *
 * The sync will not do this on its own. A completed task that has aged out of
 * Todoist's ~3-month window looks identical to one that was deleted, and
 * guessing wrong destroys history the mirror exists to hold. So removal is a
 * decision you make, with a count in front of you.
 */

import { useState } from "react";

import { useToast } from "@/context/ToastContext";
import { appMode } from "@/lib/pocketbase";
import { findStale, purgeStale, type StaleReport } from "@/lib/todoistSync";

export function CleanupCard() {
  const toast = useToast();
  const [report, setReport] = useState<StaleReport | null>(null);
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState(true);
  const [entries, setEntries] = useState(false);

  async function check() {
    setBusy(true);
    try {
      setReport(await findStale());
    } catch (err) {
      toast(`Could not check — ${err instanceof Error ? err.message : "unknown error"}`);
    } finally {
      setBusy(false);
    }
  }

  async function run() {
    if (!report) return;
    setBusy(true);
    try {
      const done = await purgeStale({ items, entries });
      toast(`Removed ${done.items} task${done.items === 1 ? "" : "s"}` +
        (entries ? ` and ${done.entries} time ${done.entries === 1 ? "entry" : "entries"}` : ""));
      setReport(null);
    } catch (err) {
      toast(`Cleanup failed — ${err instanceof Error ? err.message : "unknown error"}`);
    } finally {
      setBusy(false);
    }
  }

  if (appMode === "demo") {
    return <span style={{ fontSize: 12.5, color: "var(--muted2)" }}>Not available in demo mode.</span>;
  }

  if (!report) {
    return (
      <button onClick={() => void check()} disabled={busy} style={btn}>
        {busy ? "Checking…" : "Check for stale work"}
      </button>
    );
  }

  const nothing = report.staleItems.length === 0 && report.orphanedEntries === 0;
  if (nothing) {
    return <span style={{ fontSize: 12.5, color: "var(--good)" }}>Nothing stale — the mirror matches Todoist.</span>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end" }}>
      {report.staleItems.length > 0 && (
        <label style={row}>
          <input type="checkbox" checked={items} onChange={(e) => setItems(e.target.checked)} />
          {report.staleItems.length} finished task{report.staleItems.length === 1 ? "" : "s"} Todoist no longer has
        </label>
      )}
      {report.orphanedEntries > 0 && (
        <label style={row}>
          <input type="checkbox" checked={entries} onChange={(e) => setEntries(e.target.checked)} />
          {report.orphanedEntries} orphaned time {report.orphanedEntries === 1 ? "entry" : "entries"}
          {/* Their hours already count toward the project, so deleting them
              changes totals you may have invoiced. Off by default. */}
          <span style={{ color: "var(--muted2)" }}>— hours still count; deleting changes totals</span>
        </label>
      )}
      <span style={{ display: "flex", gap: 8 }}>
        <button onClick={() => setReport(null)} style={btn}>Cancel</button>
        <button onClick={() => void run()} disabled={busy || (!items && !entries)} style={{ ...btn, color: "var(--bad)" }}>
          {busy ? "Removing…" : "Remove"}
        </button>
      </span>
    </div>
  );
}

const row: React.CSSProperties = {
  display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, color: "var(--ink2)",
};
const btn: React.CSSProperties = {
  padding: "7px 14px", borderRadius: 999, border: "1px solid var(--line2)",
  background: "var(--surface)", color: "var(--ink2)", fontSize: 12.5, cursor: "pointer",
};
